import { createFileRoute, Outlet, useNavigate, useLocation, useRouteContext } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { usePostHog } from '@posthog/react';
import { Sidebar } from '@/components/layout/Sidebar';
import { Topbar } from '@/components/layout/Topbar';
import { useUserOrganizations } from '@/hooks/useOrganization';
import { useEffect } from 'react';
import { safeEnsureQueryData } from '@/lib/auth-helpers';
import { useClerkSession } from '@/hooks/useClerkSession';

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ location }) => {
    // Basic validation: Clerk authentication is handled at the component level
    // because it requires client-side JavaScript. This beforeLoad can only do
    // basic checks that don't require Clerk token.
    
    // Note: Actual authentication check happens in component via useAuth()
    // This beforeLoad is mainly for future extensibility
    
    return {
      // Return location for potential redirects
      intendedPath: location.pathname,
    };
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { isSignedIn, isLoaded, userId } = useAuth();
  const posthog = usePostHog();
  const navigate = useNavigate();
  const location = useLocation();
  const { trpc, queryClient } = useRouteContext({ from: '__root__' });
  const { sessionReady } = useClerkSession();
  
  // Use the hook that properly handles Clerk timing and token availability
  const { organizations, isLoading: isLoadingOrgs } = useUserOrganizations();
  
  // Identify user in PostHog when authenticated
  useEffect(() => {
    if (isLoaded && isSignedIn && userId && posthog) {
      posthog.identify(userId, {
        // Add any additional user properties here if needed
      });
    }
  }, [isLoaded, isSignedIn, userId, posthog]);
  
  // Preload organizations data once Clerk is loaded and user is signed in
  // This ensures the token is available before making the request
  useEffect(() => {
    if (isLoaded && isSignedIn && sessionReady) {
      safeEnsureQueryData(
        queryClient,
        trpc.organizations.getAll.queryOptions(undefined),
      ).catch((error) => {
        console.warn('[AuthenticatedLayout] Failed to preload organizations:', error);
      });
    }
  }, [isLoaded, isSignedIn, sessionReady, trpc, queryClient]);

  // Check if user needs onboarding (no organizations)
  useEffect(() => {
    if (isLoaded && isSignedIn && organizations !== undefined && !isLoadingOrgs) {
      const isOnboarding = location.pathname === '/onboarding';
      const isAuthenticatedRoot = location.pathname === '/_authenticated';
      const hasOrganizations = organizations && organizations.length > 0;
      // Solo redirige a onboarding si el usuario está exactamente en la raíz de la zona autenticada
      if (!hasOrganizations && isAuthenticatedRoot) {
        navigate({ to: '/onboarding', replace: true });
      }
      // Solo redirige a dashboard si está en onboarding
      if (hasOrganizations && isOnboarding) {
        navigate({ to: '/dashboard', replace: true });
      }
    }
  }, [isLoaded, isSignedIn, organizations, isLoadingOrgs, location.pathname, navigate]);

  // Redirect to home if not authenticated
  if (isLoaded && !isSignedIn) {
    window.location.href = '/';
    return null;
  }

  if (!isLoaded || !sessionReady || isLoadingOrgs) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // All authenticated routes now use the main layout with sidebar/topbar
  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <div className="ml-64 flex flex-col min-h-screen">
        <Topbar />
        <main className="flex-1 p-6 w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

